import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { canAccess } from "../hooks/usePermission";

const BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.magnirauniverse.com";

const ClientDetail = ({ selectedProject }) => {
  const navigate = useNavigate();
  const getClientNameFromProject = (projectName = "") => {
    return projectName.replace(/^\d{2}-\d{4}-\d{2}[_\s-]+/, "").trim();
  };
  const [loading, setLoading] = useState(true);
  const [salesManagers, setSalesManagers] = useState([]);
  const [assignedSalesManager, setAssignedSalesManager] = useState("");

  const [clientDetails, setClientDetails] = useState({
    ClientName: "",
    clientAddress: "",
    phoneNumber: "",
    email: "",
    dcId: "",
    possessionDate: "",
    homeType: "",
    panCardNumber: "",
    gstNumber: "",
    siteAddress: "",
    expectedCompletionDate: "",
    projectStartDate: "",
    clientBudget: "",
  });

  const canViewAll = canAccess("clientDetail", "viewAll");

  const canView =
    canAccess("clientDetail", "view") ||
    canViewAll ||
    canAccess("clientDetail", "clientDetailView");

  const canEdit =
    canAccess("clientDetail", "edit") ||
    canAccess("clientDetail", "clientDetailEdit");

  const canSave =
    canAccess("clientDetail", "create") ||
    canAccess("clientDetail", "clientDetailSave");

  const hasFieldPermission = (permissionKey) => {
    return canViewAll || canAccess("clientDetail", permissionKey);
  };

  const canShowClientName = hasFieldPermission("clientName");
  const canShowClientAddress = hasFieldPermission("clientAddress");
  const canShowPhone = hasFieldPermission("phoneNumber");
  const canShowEmail = hasFieldPermission("email");
  const canShowDcId = hasFieldPermission("dcId");
  const canShowPossessionDate = hasFieldPermission("possessionDate");
  const canShowSiteAddress = hasFieldPermission("siteAddress");
  const canShowProjectStartDate = hasFieldPermission("projectStartDate");
  const canShowExpectedCompletionDate = hasFieldPermission(
    "expectedCompletionDate",
  );
  const canShowHomeType = hasFieldPermission("homeType");
  const canShowBudget = hasFieldPermission("clientDetailShowBudget");
  const canShowPAN = hasFieldPermission("clientDetailShowPAN");
  const canShowGST = hasFieldPermission("clientDetailShowGST");
  const canAssignSalesManager = hasFieldPermission(
    "clientDetailAssignSalesManager",
  );

  const allFields = [
    ...(canShowClientName ? ["ClientName"] : []),
    ...(canShowClientAddress ? ["clientAddress"] : []),
    ...(canShowPhone ? ["phoneNumber"] : []),
    ...(canShowEmail ? ["email"] : []),
    ...(canShowDcId ? ["dcId"] : []),
    ...(canShowPossessionDate ? ["possessionDate"] : []),
    ...(canShowPAN ? ["panCardNumber"] : []),
    ...(canShowGST ? ["gstNumber"] : []),
    ...(canShowSiteAddress ? ["siteAddress"] : []),
    ...(canShowProjectStartDate ? ["projectStartDate"] : []),
    ...(canShowExpectedCompletionDate ? ["expectedCompletionDate"] : []),
    ...(canShowBudget ? ["clientBudget"] : []),
  ];

  useEffect(() => {
    if (!selectedProject?.name) {
      setLoading(false);
      return;
    }
    setClientDetails((prev) => ({
      ...prev,
      ClientName: prev.ClientName || selectedProject.name,
    }));
    const fetchClientDetails = async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/api/client-details/${encodeURIComponent(
            selectedProject.name,
          )}`,
        );

        if (res.data?.clientDetails) {
          setClientDetails((prev) => ({
            ...prev,
            ...res.data.clientDetails,
            ClientName:
              res.data.clientDetails.ClientName || selectedProject.name,
          }));
        }

        if (res.data?.assignedSalesManager) {
          setAssignedSalesManager(res.data.assignedSalesManager);
        }
      } catch (err) {
        console.warn("ℹ No existing client details found");
      } finally {
        setLoading(false);
      }
    };

    fetchClientDetails();
  }, [selectedProject]);

  useEffect(() => {
    axios
      .get(`${BASE_URL}/api/users?role=sales_manager`)
      .then((res) => setSalesManagers(res.data || []))
      .catch((err) => console.error("❌ Sales manager fetch error:", err));
  }, []);

  const handleSaveClientDetails = async () => {
    if (!canSave) {
      alert("You do not have permission to save client details");
      return;
    }

    if (!selectedProject?.name) {
      alert("Project not selected");
      return;
    }

    try {
      console.log("SAVE URL:", `${BASE_URL}/api/client-details/save`);
      const res = await axios.post(`${BASE_URL}/api/client-details/save`, {
        projectName: selectedProject.name,
        clientDetails,
        assignedSalesManager,
      });

      if (res.data?.clientDetails) {
        setClientDetails((prev) => ({
          ...prev,
          ...res.data.clientDetails,
        }));
      }

      if (res.data?.assignedSalesManager) {
        setAssignedSalesManager(res.data.assignedSalesManager);
      }

      alert("✅ Client details saved successfully");
    } catch (err) {
      console.error(
        "❌ Error saving client details:",
        err.response?.data || err,
      );
      alert(err.response?.data?.error || "❌ Failed to save client details!");
    }
  };

  const getLabel = (field) => {
    if (field === "ClientName") return "Client Name";
    if (field === "clientAddress") return "Client Address";
    if (field === "phoneNumber") return "Phone Number";
    if (field === "email") return "Email";
    if (field === "dcId") return "DC ID";
    if (field === "possessionDate") return "Possession Date";
    if (field === "panCardNumber") return "PAN Card Number";
    if (field === "gstNumber") return "GST Number";
    if (field === "siteAddress") return "Site Address";
    if (field === "projectStartDate") return "Project Start Date";
    if (field === "expectedCompletionDate") return "Expected Completion Date";
    if (field === "clientBudget") return "Client Budget (₹)";
    return field.replace(/([A-Z])/g, " $1");
  };

  if (!canView) {
    return (
      <div style={styles.container}>
        <div style={styles.headerBar}>
          <button
            onClick={() => navigate("/project")}
            style={styles.backButton}
          >
            ← Back
          </button>
          <h2 style={styles.pageTitle}>Access Denied</h2>
          <div style={{ width: "80px" }} />
        </div>

        <p style={{ textAlign: "center", color: "red", fontWeight: 700 }}>
          You do not have permission to view client details.
        </p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.headerBar}>
        <button onClick={() => navigate("/project")} style={styles.backButton}>
          ← Back
        </button>
        <h2 style={styles.pageTitle}>Client Details</h2>
        <div style={{ width: "80px" }} />
      </div>

      {loading ? (
        <p style={styles.loading}>⏳ Loading client details...</p>
      ) : (
        <form style={styles.form}>
          {allFields.map((field) => (
            <div key={field} style={styles.formGroup}>
              <label style={styles.label}>{getLabel(field)}</label>

              <input
                type={
                  field === "clientBudget"
                    ? "number"
                    : field.toLowerCase().includes("date")
                      ? "date"
                      : "text"
                }
                value={clientDetails[field] || ""}
                disabled={!canEdit}
                onChange={(e) =>
                  setClientDetails({
                    ...clientDetails,
                    [field]: e.target.value,
                  })
                }
                style={{
                  ...styles.input,
                  backgroundColor: canEdit ? "#fff" : "#f1f1f1",
                  cursor: canEdit ? "text" : "not-allowed",
                }}
                placeholder={
                  field === "clientBudget" ? "Enter budget in INR" : ""
                }
              />
            </div>
          ))}

          {canShowHomeType && (
            <div style={styles.formGroup}>
              <label style={styles.label}>Home Type</label>
              <select
                value={clientDetails.homeType}
                disabled={!canEdit}
                onChange={(e) =>
                  setClientDetails({
                    ...clientDetails,
                    homeType: e.target.value,
                  })
                }
                style={{
                  ...styles.input,
                  backgroundColor: canEdit ? "#fff" : "#f1f1f1",
                  cursor: canEdit ? "pointer" : "not-allowed",
                }}
              >
                <option value="">-- Select --</option>
                <option value="1BHK">1BHK</option>
                <option value="2BHK">2BHK</option>
                <option value="3BHK">3BHK</option>
                <option value="4BHK">4BHK</option>
                <option value="5BHK">5BHK</option>
                <option value="Bunglow">Bunglow</option>
              </select>
            </div>
          )}

          {canAssignSalesManager && (
            <div style={styles.formGroup}>
              <label style={styles.label}>Assign Sales Manager</label>
              <select
                value={assignedSalesManager}
                disabled={!canEdit}
                onChange={(e) => setAssignedSalesManager(e.target.value)}
                style={{
                  ...styles.input,
                  backgroundColor: canEdit ? "#fff" : "#f1f1f1",
                  cursor: canEdit ? "pointer" : "not-allowed",
                }}
              >
                <option value="">-- Select Sales Manager --</option>
                {salesManagers.map((sm) => (
                  <option key={sm._id} value={sm._id}>
                    {sm.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {canSave && (
            <button
              type="button"
              onClick={handleSaveClientDetails}
              style={styles.saveButton}
            >
              💾 Save Client Details
            </button>
          )}
        </form>
      )}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: "700px",
    margin: "40px auto",
    padding: "20px 30px",
    background: "#f9f9f9",
    borderRadius: "12px",
    boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
    fontFamily: "Calibri, Segoe UI, sans-serif",
  },
  headerBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: "1px solid #ddd",
    paddingBottom: "10px",
    marginBottom: "25px",
  },
  backButton: {
    background: "#fff",
    padding: "6px 14px",
    border: "1px solid #ccc",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "600",
  },
  pageTitle: {
    fontSize: "22px",
    fontWeight: "700",
  },
  loading: {
    textAlign: "center",
    fontSize: "18px",
    marginTop: "50px",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  formGroup: {
    marginBottom: "10px",
  },
  label: {
    fontWeight: "600",
    marginBottom: "5px",
    display: "block",
  },
  input: {
    width: "100%",
    padding: "10px",
    borderRadius: "6px",
    border: "1px solid #ccc",
  },
  saveButton: {
    marginTop: "20px",
    padding: "12px",
    backgroundColor: "#007bff",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
  },
};

export default ClientDetail;
